import { useCallback, useEffect, useRef, useState } from 'react'
import {
  fetchReservationsForMonth,
  getMonthWindow,
  YearMonth,
  yearMonthKey,
} from '../api/listByStatusRequest'
import {
  AvailabilityCalendarEvent,
  AvailabilityCalendarRoom,
} from '../AvailabilityCalendarModal/types'

interface UsePublicReservationsByStatusParams {
  enabled: boolean
  focusedMonth: YearMonth
  rooms: AvailabilityCalendarRoom[]
  unavailableTitle: string
}

const flattenCache = (
  cache: Map<string, AvailabilityCalendarEvent[]>
): AvailabilityCalendarEvent[] => {
  const events: AvailabilityCalendarEvent[] = []
  const seen = new Set<string>()

  cache.forEach(monthEvents => {
    monthEvents.forEach(event => {
      if (seen.has(event.id)) return
      seen.add(event.id)
      events.push(event)
    })
  })

  return events.sort((a, b) => a.start.getTime() - b.start.getTime())
}

export const usePublicReservationsByStatus = ({
  enabled,
  focusedMonth,
  rooms,
  unavailableTitle,
}: UsePublicReservationsByStatusParams) => {
  const cacheRef = useRef(new Map<string, AvailabilityCalendarEvent[]>())
  const inFlightRef = useRef(new Map<string, Promise<AvailabilityCalendarEvent[]>>())
  const roomsKeyRef = useRef('')
  const [events, setEvents] = useState<AvailabilityCalendarEvent[]>([])
  const [isLoading, setIsLoading] = useState(false)
  const [isError, setIsError] = useState(false)

  const roomsKey = rooms.map(room => room.id).join('|')

  const syncEventsFromCache = useCallback((): void => {
    setEvents(flattenCache(cacheRef.current))
  }, [])

  const clearCache = useCallback((): void => {
    cacheRef.current.clear()
    inFlightRef.current.clear()
    setEvents([])
    setIsError(false)
    setIsLoading(false)
  }, [])

  const loadMissingMonths = useCallback(
    async (months: YearMonth[], force = false): Promise<void> => {
      if (rooms.length === 0) {
        setEvents([])
        return
      }

      const missing = months.filter(month => {
        const key = yearMonthKey(month)
        if (force) return true
        return !cacheRef.current.has(key)
      })

      if (missing.length === 0) {
        syncEventsFromCache()
        setIsError(false)
        return
      }

      const isInitialLoad = cacheRef.current.size === 0
      if (isInitialLoad) {
        setIsLoading(true)
      }
      setIsError(false)

      try {
        await Promise.all(
          missing.map(async month => {
            const key = yearMonthKey(month)

            if (!force) {
              const existing = inFlightRef.current.get(key)
              if (existing != null) {
                await existing
                return
              }
            }

            const request = fetchReservationsForMonth(month, unavailableTitle, rooms)
              .then(monthEvents => {
                cacheRef.current.set(key, monthEvents)
                return monthEvents
              })
              .finally(() => {
                inFlightRef.current.delete(key)
              })

            inFlightRef.current.set(key, request)
            await request
          })
        )

        syncEventsFromCache()
        setIsError(false)
      } catch {
        setIsError(true)
      } finally {
        setIsLoading(false)
      }
    },
    [rooms, unavailableTitle, syncEventsFromCache]
  )

  useEffect(() => {
    if (!enabled) {
      roomsKeyRef.current = ''
      clearCache()
      return
    }

    if (roomsKeyRef.current !== roomsKey) {
      roomsKeyRef.current = roomsKey
      cacheRef.current.clear()
      inFlightRef.current.clear()
    }

    void loadMissingMonths(getMonthWindow(focusedMonth))
  }, [enabled, focusedMonth.year, focusedMonth.month, roomsKey, loadMissingMonths, clearCache])

  const refetch = useCallback((): void => {
    if (!enabled) return
    const windowMonths = getMonthWindow(focusedMonth)
    windowMonths.forEach(month => {
      cacheRef.current.delete(yearMonthKey(month))
    })
    void loadMissingMonths(windowMonths, true)
  }, [enabled, focusedMonth, loadMissingMonths])

  return {
    events,
    isLoading,
    isError,
    refetch,
  }
}
